import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, KeyRound, Phone, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: enter phone, 2: enter OTP, 3: new password
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setOtpSending(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/send-otp`, {
        phone: phoneNumber
      });
      
      if (response.data.success) {
        toast.success('OTP sent to your phone number!');
        setForgotStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      toast.error('Please enter the OTP');
      return;
    }

    setOtpVerifying(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/verify-otp`, {
        phone: phoneNumber,
        otp: otp
      });
      
      if (response.data.success) {
        toast.success('OTP verified! Set your new password.');
        setResetToken(response.data.reset_token);
        setForgotStep(3);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/reset`, {
        phone: phoneNumber,
        reset_token: resetToken,
        new_password: newPassword
      });
      
      if (response.data.success) {
        toast.success('Password reset successful! Please login.');
        setShowForgotPassword(false);
        setForgotStep(1);
        setPhoneNumber('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setResetToken('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setPhoneNumber('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResetToken('');
  };

  // Forgot Password UI
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center pb-4">
            <h1 className="text-3xl font-extrabold text-[#7FA1C3] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Convella
            </h1>
            <p className="text-sm text-slate-600">The Taste of Real Ice Cream</p>
            <CardTitle className="text-xl mt-4 flex items-center justify-center gap-2">
              <KeyRound size={24} className="text-[#7FA1C3]" />
              Reset Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Step 1: Enter Phone Number */}
            {forgotStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center mb-4">
                  Enter your registered phone number to receive OTP
                </p>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="phone"
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="Enter phone number"
                      className="pl-10"
                      maxLength={10}
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSendOtp}
                  disabled={otpSending}
                  className="w-full bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full py-5"
                >
                  {otpSending ? 'Sending OTP...' : 'Send OTP'}
                </Button>
                <Button
                  onClick={handleBackToLogin}
                  variant="ghost"
                  className="w-full text-slate-600"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Login
                </Button>
              </div>
            )}

            {/* Step 2: Enter OTP */}
            {forgotStep === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center mb-4">
                  Enter the OTP sent to <strong>{phoneNumber}</strong>
                </p>
                <div>
                  <Label htmlFor="otp">OTP Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit OTP"
                    className="mt-2 text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                </div>
                <Button
                  onClick={handleVerifyOtp}
                  disabled={otpVerifying}
                  className="w-full bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full py-5"
                >
                  {otpVerifying ? 'Verifying...' : 'Verify OTP'}
                </Button>
                <Button
                  onClick={() => setForgotStep(1)}
                  variant="ghost"
                  className="w-full text-slate-600"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Change Phone Number
                </Button>
              </div>
            )}

            {/* Step 3: Set New Password */}
            {forgotStep === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center mb-4">
                  Create a new password for your account
                </p>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="mt-2"
                  />
                </div>
                <Button
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-5"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Login UI
  return (
    <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-8">
          <h1 className="text-4xl font-extrabold text-[#7FA1C3] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Convella
          </h1>
          <p className="text-sm text-slate-600">The Taste of Real Ice Cream</p>
          <CardTitle className="text-2xl mt-6">Welcome Back</CardTitle>
          <p className="text-sm text-slate-500 mt-2">Login to access your dashboard</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="mt-2"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#7FA1C3] hover:text-[#6482A6] hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full py-6 text-lg"
            >
              <LogIn size={20} className="mr-2" />
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <p className="text-center text-sm text-slate-500 mt-4">
              Only authorized users can access this system.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
